import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import PushList from './PushList';
import PushDetail from './PushDetail';

const PushManage = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState('all');
  const [targetUserId, setTargetUserId] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [tokenCount, setTokenCount] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // 목록/상세 화면 전환
  const [activeView, setActiveView] = useState('list'); // 'list' 또는 메시지 ID
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchTokenCount = async () => {
    setTokenLoading(true);
    try {
      // notification_enabled = true 인 사용자 토큰만 집계 (허용한 사람에게만 발송 기준)
      const { count, error } = await supabase
        .from('v_fcm_tokens_notification_enabled')
        .select('*', { count: 'exact', head: true });
      if (error) throw error;
      setTokenCount(count ?? 0);
    } catch (e) {
      console.error(e);
      setTokenCount(0);
    } finally {
      setTokenLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      // 푸시 알람 허용(notification_enabled = true) 사용자만 조회
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, user_settings!inner(notification_enabled)')
        .eq('user_settings.notification_enabled', true)
        .order('name');
      if (error) throw error;
      setUsers(data || []);
    } catch (e) {
      console.error(e);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchTokenCount();
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setMessage({ type: 'danger', text: '제목을 입력해주세요.' });
      return;
    }
    if (targetType === 'user' && !targetUserId) {
      setMessage({ type: 'danger', text: '대상 사용자를 선택해주세요.' });
      return;
    }

    setSending(true);
    setMessage({ type: '', text: '' });
    try {
      const { data: { user: adminUser } } = await supabase.auth.getUser();
      const { data: row, error: insertError } = await supabase
        .from('push_messages')
        .insert({
          title: title.trim(),
          body: body.trim() || null,
          target_type: targetType,
          target_user_id: targetType === 'user' ? targetUserId : null,
          status: 'pending',
          target_cd: null,
          created_by: adminUser?.id || null,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      try {
        const { data: fnData, error: fnError } = await supabase.functions.invoke('send-push', {
          body: {
            push_message_id: row?.id,
            title: title.trim(),
            body: body.trim() || null,
            target_type: targetType,
            target_user_id: targetType === 'user' ? targetUserId : null,
          },
        });

        if (fnError) {
          // FunctionsHttpError: context.status(404=미배포, 502=함수 오류), context.body 등
          const ctx = fnError.context;
          const status = ctx?.status ?? '';
          const body = ctx?.body ?? fnData?.error ?? '';
          const detail = [status && `상태 ${status}`, body].filter(Boolean).join(' · ');
          const errMsg = fnError.message || 'Edge Function 호출 실패';
          setMessage({
            type: 'warning',
            text: `발송 이력은 저장되었습니다. 푸시 발송 실패: ${errMsg}${detail ? ` (${detail})` : ''}. 배포·시크릿 확인 또는 브라우저 개발자도구 → Network에서 "send-push" 요청 상태코드 확인.`,
          });
        } else if (fnData?.error) {
          setMessage({
            type: 'warning',
            text: `발송 이력은 저장되었습니다. 푸시 발송 실패: ${fnData.error}.`,
          });
        } else {
          const success = fnData?.success_count ?? 0;
          const fail = fnData?.fail_count ?? 0;
          setMessage({
            type: 'success',
            text: `바로 발송 완료. 성공 ${success}건, 실패 ${fail}건.`,
          });
        }
      } catch (e) {
        const errMsg = e?.message || e?.toString?.() || '알 수 없는 오류';
        setMessage({
          type: 'warning',
          text: `발송 이력은 저장되었습니다. 푸시 발송 예외: ${errMsg}. (send-push 배포·시크릿 확인)`,
        });
      }

      setTitle('');
      setBody('');
      setTargetUserId('');
      setRefreshKey(prev => prev + 1); // 목록 새로고침
    } catch (err) {
      setMessage({ type: 'danger', text: err.message || '저장 중 오류가 발생했습니다.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">앱 푸시 메시지 관리</h1>

      <div className="alert alert-info mb-4" role="alert">
        <strong>Firebase 연동 방식</strong><br />
        푸시 발송은 보안상 브라우저에서 Firebase에 직접 요청할 수 없습니다. (서비스 계정 비밀키 노출 방지)
        <strong> Supabase Edge Function &quot;send-push&quot;</strong>가 Firebase FCM HTTP v1으로 발송합니다.
        실제 발송이 되려면 missionqt-app 저장소의 <code>supabase/functions/send-push</code>를 배포하고,
        Supabase 시크릿에 <code>FIREBASE_SERVICE_ACCOUNT_JSON</code>(Firebase 서비스 계정 JSON)을 설정하세요.
      </div>

      <div className="card border-success mb-4">
        <div className="card-header py-2 bg-light">
          <h6 className="m-0 font-weight-bold text-success">발송 방식 안내</h6>
        </div>
        <div className="card-body py-2 small">
          <ul className="mb-0 pl-3">
            <li><strong>즉시 발송:</strong> "새 메시지 추가" 또는 목록의 "즉시 발송" 버튼으로 지금 바로 발송</li>
            <li><strong>정기 발송:</strong> 메시지 상태를 "아침", "점심", "저녁"으로 설정하면 Supabase 크론탭에서 자동 발송</li>
          </ul>
        </div>
      </div>

      {/* 토큰 현황 */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">푸시 대상 현황</h6>
        </div>
        <div className="card-body">
          {tokenLoading ? (
            <p className="mb-0">조회 중...</p>
          ) : (
            <p className="mb-0">
              <strong>등록된 기기 수 (FCM 토큰):</strong> <span className="text-primary">{tokenCount}개</span>
            </p>
          )}
        </div>
      </div>

      {/* 탭 메뉴 */}
      <ul className="nav nav-tabs mb-4" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeView === 'list' ? 'active' : ''}`}
            onClick={() => setActiveView('list')}
            type="button"
            role="tab"
          >
            <i className="fas fa-list"></i> 메시지 목록
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeView === 'new' ? 'active' : ''}`}
            onClick={() => setActiveView('new')}
            type="button"
            role="tab"
          >
            <i className="fas fa-plus"></i> 새 메시지 추가
          </button>
        </li>
      </ul>

      {/* 메시지 목록 화면 */}
      {activeView === 'list' && (
        <PushList
          onSelectMessage={(id) => setActiveView(id)}
          onRefresh={() => setRefreshKey(prev => prev + 1)}
          refreshKey={refreshKey}
        />
      )}

      {/* 새 메시지 추가 화면 */}
      {activeView === 'new' && (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">새 메시지 추가</h6>
          </div>
          <div className="card-body">
            {message.text && (
              <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                {message.text}
                <button type="button" className="close" onClick={() => setMessage({ type: '', text: '' })}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="push-title" className="form-label">제목 *</label>
                <input
                  id="push-title"
                  type="text"
                  className="form-control"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="알림 제목"
                  maxLength={200}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="push-body" className="form-label">내용</label>
                <textarea
                  id="push-body"
                  className="form-control"
                  rows={4}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="알림 본문"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">대상</label>
                <div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="targetType"
                      id="target-all"
                      checked={targetType === 'all'}
                      onChange={() => setTargetType('all')}
                    />
                    <label className="form-check-label" htmlFor="target-all">전체</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="targetType"
                      id="target-user"
                      checked={targetType === 'user'}
                      onChange={() => setTargetType('user')}
                    />
                    <label className="form-check-label" htmlFor="target-user">특정 사용자</label>
                  </div>
                </div>
              </div>
              {targetType === 'user' && (
                <div className="mb-3">
                  <label htmlFor="push-target-user" className="form-label">사용자 선택</label>
                  <select
                    id="push-target-user"
                    className="form-control"
                    value={targetUserId}
                    onChange={(e) => setTargetUserId(e.target.value)}
                  >
                    <option value="">선택하세요</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name || u.email || u.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mb-3">
                <button type="submit" className="btn btn-primary" disabled={sending}>
                  {sending ? '처리 중...' : '즉시 발송'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 메시지 상세 화면 */}
      {typeof activeView === 'string' && activeView !== 'list' && activeView !== 'new' && (
        <PushDetail
          messageId={activeView}
          onBack={() => setActiveView('list')}
          onRefresh={() => setRefreshKey(prev => prev + 1)}
        />
      )}
    </>
  );
};

export default PushManage;
